import type { CountItemView } from '@/api/endpoints/inventory-count-sessions'
import {
  addStockQuantities,
  formatStockDisplay,
  subtractStockQuantities,
} from '@/shared/lib/stock-quantity'

/*
 * Quantities may carry up to 3 decimals (CHG-2026-09-stock-usage-and-price-twins): every sum here
 * runs in integer thousandths and every figure renders through formatStockDisplay, so a whole
 * number comes out exactly as it did before.
 */

/** The physical on-hand the count was checked against: expected available + reserved. */
export function expectedOnHand(item: CountItemView): number {
  return addStockQuantities(item.expectedAvailable, item.expectedReserved)
}

/** How much a confirmed correction moved, for the reopen sheet: |counted − expected|. */
export function bookedAmount(item: CountItemView): number {
  return Math.abs(
    subtractStockQuantities(item.countedQty ?? 0, expectedOnHand(item)),
  )
}

/**
 * The three row groups the detail table sorts by (variances first, then not-counted, then
 * matches) and the filter chips key off. A `CONFIRMED_ZERO`/`SKIPPED` row always has
 * `countedQty === null` (it was never physically counted), which is what makes it "not counted"
 * regardless of how it was later resolved.
 */
export type CountItemGroup = 'variance' | 'notCounted' | 'match'

export function countItemGroup(item: CountItemView): CountItemGroup {
  if (item.countedQty == null) return 'notCounted'
  return item.delta !== 0 ? 'variance' : 'match'
}

/**
 * The delta actually applied to stock, including a `CONFIRMED_ZERO` row whose API `delta` is
 * `null` (it was never captured): its effective delta is `-(expectedAvailable + expectedReserved)`,
 * matching the backend's history-summary computation. Only meaningful for confirmed items — used
 * to compute the "Korrektur" total, never for the per-row Diff cell (which shows "—" for a never
 * counted row, `CONFIRMED_ZERO` included).
 */
export function effectiveConfirmedDelta(item: CountItemView): number {
  if (item.delta != null) return item.delta
  if (item.resolution === 'CONFIRMED_ZERO') {
    return subtractStockQuantities(0, expectedOnHand(item))
  }
  return 0
}

export function isConfirmedItem(item: CountItemView): boolean {
  return item.resolution === 'CONFIRMED' || item.resolution === 'CONFIRMED_ZERO'
}

/** Sums the positive/negative confirmed deltas of a session's items — the "Korrektur" figures. */
export function sumCorrections(items: Array<CountItemView>): {
  up: number
  down: number
} {
  let up = 0
  let down = 0
  for (const item of items) {
    if (!isConfirmedItem(item)) continue
    const delta = effectiveConfirmedDelta(item)
    if (delta > 0) up = addStockQuantities(up, delta)
    else if (delta < 0) down = addStockQuantities(down, delta)
  }
  return { up, down }
}

/** "+17 / −7", one-sided when only one direction occurred, "—" when there is nothing to correct. */
export function formatCorrection(up: number, down: number): string {
  if (up === 0 && down === 0) return '—'
  const upText = formatStockDisplay(up)
  const downText = formatStockDisplay(Math.abs(down))
  if (down === 0) return `+${upText}`
  if (up === 0) return `−${downText}`
  return `+${upText} / −${downText}`
}
